/**
 * Fetch browser safe config from the server
 * @module services/config
 */
import get from 'lodash/get'
import isFinite from 'lodash/isFinite'
import isUndefined from 'lodash/isUndefined'
import omit from 'lodash/omit'
import parseInt from 'lodash/parseInt'
import { map as mapPromise } from 'bluebird'
import { fromJS, Map, List } from 'immutable'
import strictUriEncode from 'strict-uri-encode'
import request from '../request'
import { dataUriToBlob } from '../../data-uri'
import { createAuthorization } from '../auth'
import { getContent } from '../content'
import { isValidContentType } from '../content/content-types'
import {
  getD6ContentType,
  isTermContentType,
  D6_CONTENT_TYPE_TERM,
} from '../content/d6-content-types'
import { getNode } from '../node'
/**
 * Publish an image to Brooklyn images service using superagent's multipart support for larger
 * image sizes
 * @param {Object} param0 The params
 * @param {Map} param0.auth The auth data
 * @param {String} param0.server The server url
 * @param {Map} param0.image The image data to publish
 * @param {Boolean} [param0.processImage=true] When true the create endpoint will process the image
 * (set to false when using a preprocessed image)
 * @returns {Promise<Map>} The published image data
 */
export async function createImage ({ auth, server, image, processImage = true } = {}) {
  const imageBlob = dataUriToBlob(image.get('url'))
  const contentRelationship = image.get('contentRelationship')
  const res = await request
    .post(`${server}/v1/image`)
    .attach('imageFile', imageBlob, image.get('originalFileName'))
    .field({
      originalFileName: image.get('originalFileName'),
      processImage,
      ...(contentRelationship &&
        contentRelationship.get('type') && {
          'contentRelationship[type]': contentRelationship.get('type'),
          'contentRelationship[language]': contentRelationship.get('language'),
          'contentRelationship[d6Id]': contentRelationship.get('d6Id'),
          'contentRelationship[d6ContentType]': contentRelationship.get('d6ContentType'),
          'contentRelationship[isSource]': contentRelationship.get('isSource'),
        }),
    })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Persist one slot from a source still after its assets-render preview is ready.
 * Does not expand the closest-slot matrix.
 */
export async function persistImageSlotFromSource ({
  auth,
  server,
  sourceImageId,
  seed,
  contentRelationship,
} = {}) {
  const res = await request
    .post(`${server}/v1/image/persist-slot`)
    .send({
      sourceImageId,
      seed,
      contentRelationship:
        contentRelationship && contentRelationship.toJS
          ? contentRelationship.toJS()
          : contentRelationship,
    })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Attach a source still to a primary slot and persist closest derived rasters.
 */
export async function placeImageFromSource ({
  auth,
  server,
  sourceImageId,
  primaryType,
  language,
  d6Id,
  d6ContentType,
  skipTypes,
  seeds,
  contentType,
  squareSource,
  isSource,
} = {}) {
  const res = await request
    .post(`${server}/v1/image/place-from-source`)
    .send({
      sourceImageId,
      primaryType,
      language,
      d6Id,
      d6ContentType,
      skipTypes: skipTypes && skipTypes.toJS ? skipTypes.toJS() : skipTypes,
      seeds: seeds && seeds.toJS ? seeds.toJS() : seeds,
      squareSource,
      contentType,
      isSource,
    })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Regenerate an Imgix gen-fill slot (or upgrade legacy display-fill).
 */
export async function regenerateImage ({ auth, server, imageId } = {}) {
  const res = await request
    .post(`${server}/v1/image/${strictUriEncode(imageId)}/regenerate`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Delete an image to Brooklyn images service
 * @param {Object} param0 The params
 * @param {Map} param0.auth The auth data
 * @param {String} param0.server The server url
 * @param {Map<string, String>} param0.image The image data to publish
 * @returns {Promise<Map>} The deleted image data
 */
export async function deleteImage ({ auth, server, image } = {}) {
  const res = await request
    .delete(`${server}/v1/image/${strictUriEncode(image.get('id'))}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Get an image based on its content relationship from brooklyn
 * @param {Object} param0 The params
 * @param {Map} param0.auth The auth data
 * @param {String} param0.server The server url
 * @param {Map<{contentRelationship: Map}>} param0.image The image data to publish
 * @returns {Map} The deleted image data
 */
export async function getImageContentRelationship ({ auth, server, image } = {}) {
  const contentRelationship = image.get('contentRelationship') || Map()
  const res = await request
    .get(`${server}/v1/image-content-relationship`)
    .query({
      d6Id: contentRelationship.get('d6Id'),
      d6ContentType: contentRelationship.get('d6ContentType'),
      language: contentRelationship.get('language'),
      type: contentRelationship.get('type'),
      isSource: contentRelationship.get('isSource'),
    })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Get an image based on its content relationship from brooklyn
 * @param {Object} param0 The params
 * @param {Map} param0.auth The auth data
 * @param {String} param0.server The server url
 * @param {Map<{contentRelationship: Map}>} param0.image The image data to publish
 * @returns {Map} The deleted image data
 */
export async function getImageContentRelationshipList ({ auth, server, image } = {}) {
  const contentRelationship = image.get('contentRelationship') || Map()
  const res = await request
    .get(`${server}/v1/image-content-relationship-list`)
    .query({
      d6Id: contentRelationship.get('d6Id'),
      d6ContentType: contentRelationship.get('d6ContentType'),
      language: contentRelationship.get('language'),
      type: contentRelationship.get('type'),
      isSource: contentRelationship.get('isSource'),
    })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Get an image list by d6id and d6ContentType and optionally language
 * @param {String} param0.d6Id The d6 ID
 * @param {String} param0.d6ContentType The d6 content type
 * @param {String} param0.language The language of the content
 * @param {Map} param0.auth The auth data
 * @param {String} param0.server The server url
 * @returns {Map} The image list
 */
export async function getImageContentRelationshipByD6IdAndD6ContentType ({
  auth,
  server,
  d6Id,
  d6ContentType,
  language,
} = {}) {
  const res = await request
    .get(`${server}/v1/image-content-relationship-list`)
    .query(
      omit(
        {
          d6Id,
          d6ContentType,
          language,
        },
        isUndefined,
      ),
    )
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Get an image based on its id from brooklyn
 * @param {Object} param0 The params
 * @param {Map} param0.auth The auth data
 * @param {String} param0.server The server url
 * @param {String} param0.id The image id
 * @returns {Promise<Map>} The image reponse
 */
export async function getImageById ({ auth, server, id } = {}) {
  const res = await request
    .get(`${server}/v1/image/${id}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Get a collection of existing images based on nodeId
 */
export async function getImagesByNodeId ({ auth, server, contentId } = {}) {
  if (!contentId || contentId.length === 0) {
    throw new Error('Please provide a contentId')
  }
  try {
    const res = await request
      .get(`${server}/v1/image-content-relationship-list`)
      .query({ d6Id: contentId, d6ContentType: 'node' })
      .set('authorization', createAuthorization(auth))
      .set('accept', 'application/json')
    const data = get(res, 'body', [])
    const result = await mapPromise(data, async (item) => {
      const nid = get(item, 'contentRelationship.d6Id')
      const node = await getNode({
        server,
        nid,
      })
      return fromJS(item)
        .set('currentImage', Map({ url: get(item, 'url'), id: get(item, 'id') }))
        .set('node', node)
        .delete('url')
    })
    return fromJS(result)
  } catch (error) {
    if (error.status === 404) {
      const err = new Error(`Content not found for ${contentId}`)
      err.status = 404
      throw err
    }
    throw error
  }
}

/**
 * Get a collection of existing images based on contentId and contentType
 * @param {Object} param0 The params
 * @param {Map} param0.auth The auth data
 * @param {String} param0.server The server url
 * @param {{contentId:Number, contentype:String}} param0.content The content of the image
 * @param {String} param0.language The content language
 */
export async function getImagesByContent ({ auth, server, content = {}, language } = {}) {
  const { contentType } = content
  let { contentId } = content
  contentId = parseInt(contentId)
  if (!contentId || !isFinite(contentId)) {
    throw new Error('Please provide a contentId')
  }
  if (!contentType || !isValidContentType(contentType)) {
    throw new Error('Please provide a valid contentType')
  }
  try {
    const res = await request
      .get(`${server}/v1/image-content-relationship-list`)
      .query({ d6Id: contentId, d6ContentType: getD6ContentType(contentType) })
      .set('authorization', createAuthorization(auth))
      .set('accept', 'application/json')
    const data = get(res, 'body', [])
    const result = await mapPromise(data, async (item) => {
      const populatedContent = await getContent({
        auth,
        server,
        contentId,
        contentType,
        language,
      })
      return fromJS({
        ...item,
        currentImage: { url: get(item, 'url'), id: get(item, 'id') },
        content: populatedContent,
      }).delete('url')
    })
    return fromJS(result)
  } catch (error) {
    if (error.status === 404) {
      const err = new Error(`Content not found for ${contentId} with type ${contentType}`)
      err.status = 404
      throw err
    }
    throw error
  }
}

/**
 * Create a Map that can be used for a unique image content relationship comparisons,
 * this does not not include all fields
 * @param {Map} contentRelationship The image content relationship data
 * @returns {Map} A records with only fields that are unique on a image content relationship
 */
export function createImageContentRelationshipUnique (contentRelationship = Map()) {
  return Map({
    d6Id: contentRelationship.get('d6Id'),
    d6ContentType: contentRelationship.get('d6ContentType'),
    language: contentRelationship.get('language'),
    type: contentRelationship.get('type'),
    isSource: contentRelationship.get('isSource'),
  })
}

/**
 * Give an image with a content relationship and content
 * return true if the content is a match for the image
 * @param {Map<{contentRelationship: Map}>} image An image with a content relationship
 * @param {Map<{contentRelationship: Map}>} content The content to check
 * @returns {Boolean} True if image is related to this conten
 */
export function isImageContent (image, content) {
  if (!Map.isMap(image)) {
    throw new Error('The image argument is required to be a Map')
  }
  if (!Map.isMap(content)) {
    throw new Error('The content argument is required to be a Map')
  }
  const contentRelationship = image.get('contentRelationship')
  if (!contentRelationship) {
    return false
  }
  const contentTypeIsTerm = isTermContentType(content.get('contentType'))
  const typeMatch = contentTypeIsTerm
    ? contentRelationship.get('d6ContentType') === D6_CONTENT_TYPE_TERM
    : contentRelationship.get('d6ContentType') !== D6_CONTENT_TYPE_TERM
  const idMatch = parseInt(contentRelationship.get('d6Id')) === parseInt(content.get('contentId'))
  return idMatch && typeMatch
}

/**
 * Returns true if every field on an image content relationship is defined
 * @param {Map} imageContentRelationship The image content relationship
 * @return {Boolean} True if every field is defined
 */
export function isValidImageContentRelationship (imageContentRelationship = Map()) {
  const fields = List(['d6Id', 'd6ContentType', 'language', 'type', 'isSource'])
  return fields.every((field) => !isUndefined(imageContentRelationship.get(field)))
}

/**
 * Download the artwork for one content detail page as a zip.
 * Entry names are the upload filenames stored on each image.
 * @param {Object} param0 The params
 * @param {Map} param0.auth The auth data
 * @param {String} param0.server The Brooklyn server url
 * @param {Number} param0.contentId The content id
 * @param {String} param0.contentType The content type
 * @param {String[]} param0.language Languages to include, in preference order
 */
export async function downloadContentImagesArchive ({
  auth,
  server,
  contentId,
  contentType,
  language,
} = {}) {
  const fileName = `${contentId}.${contentType}.images.zip`
  const res = await request
    .get(`${server}/v1/image-content-relationship-list/archive`)
    .query({ contentId, contentType, language })
    .responseType('blob')
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/zip')
  const blobUrl = URL.createObjectURL(res.body)
  const link = document.createElement('a')
  link.setAttribute('download', fileName)
  link.setAttribute('href', blobUrl)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(blobUrl)
}
